import React from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import { DollarSign, Shield, Info, Sparkles, Building2 } from "lucide-react";
import { currencySymbol } from "shared/CurrencySymbol";

interface FinancialDetailsProps {
  propertyData: PropertyToEdit | null;
}

const FinancialDetails: React.FC<FinancialDetailsProps> = ({ propertyData }) => {
  const hasSecurityDeposit = propertyData?.security_deposit_text && 
                             propertyData.security_deposit_text.trim().length > 0;
  const hasDepositDetails = propertyData?.security_deposit_details && 
                            propertyData.security_deposit_details.trim().length > 0;
  const hasCleaningFee = propertyData?.cleaning_fee && Number(propertyData.cleaning_fee) > 0;
  const hasCityFee = propertyData?.city_fee && Number(propertyData.city_fee) > 0;

  // Don't render anything if there's nothing to show
  if (!hasSecurityDeposit && !hasCleaningFee && !hasCityFee) return null;

  // Check if the deposit is a number or custom text
  const depositValue = propertyData?.security_deposit_text || "";
  const isNumeric = !isNaN(Number(depositValue)) && depositValue.trim() !== "";
  const displayValue = isNumeric 
    ? `${currencySymbol()}${Number(depositValue).toFixed(2)}`
    : depositValue;

  return (
    <>
      {/* Security Deposit Section */}
      {hasSecurityDeposit && (
        <div className="listingSection__wrap w-full">
          {/* Header Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-xl">
                <Shield size={24} className="text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Security Deposit
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Refundable deposit information
                </p>
              </div>
            </div>
          </div>

          <div className="w-full h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent my-6" />

          {/* Content Section */}
          <div className="space-y-6">
            {/* Security Deposit Amount Card */}
            <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white dark:bg-neutral-800 rounded-xl shadow-sm">
                    <DollarSign size={24} className="text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-1">
                      Required Deposit
                    </p>
                    <p className={`${isNumeric ? 'text-3xl' : 'text-2xl'} font-bold text-gray-900 dark:text-white`}>
                      {displayValue}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Deposit Details */}
            {hasDepositDetails && (
              <div className="p-6 bg-white dark:bg-neutral-800 rounded-2xl border border-gray-200 dark:border-neutral-700">
                <div className="flex items-start gap-3 mb-4">
                  <Info size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Deposit Policy
                    </h3>
                  </div>
                </div>
                
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                    {propertyData.security_deposit_details}
                  </p>
                </div>
              </div>
            )}

            {/* Info Banner */}
            <div className="p-5 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
              <div className="flex gap-3">
                <Shield size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-gray-900 dark:text-white font-semibold mb-1.5">
                    Deposit Protection
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Your security deposit is protected and will be returned according to the terms above. 
                    Please ensure you understand the refund conditions before booking.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional Fees Section */}
      {(hasCleaningFee || hasCityFee) && (
        <div className="listingSection__wrap w-full">
          {/* Header Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                <DollarSign size={24} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Additional Fees
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Monthly fees and charges
                </p>
              </div>
            </div>
          </div>

          <div className="w-full h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent my-6" />

          {/* Content Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Cleaning Fee Card */}
            {hasCleaningFee && (
              <div className="p-6 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-2xl border border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-white dark:bg-neutral-800 rounded-xl shadow-sm">
                    <Sparkles size={20} className="text-purple-600 dark:text-purple-400" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    Cleaning Fee
                  </p>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {currencySymbol()}{Number(propertyData.cleaning_fee).toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Monthly
                </p>
              </div>
            )}

            {/* City Fee Card */}
            {hasCityFee && (
              <div className="p-6 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-2xl border border-orange-200 dark:border-orange-800">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-white dark:bg-neutral-800 rounded-xl shadow-sm">
                    <Building2 size={20} className="text-orange-600 dark:text-orange-400" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    City Fee
                  </p>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {currencySymbol()}{Number(propertyData.city_fee).toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Monthly
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FinancialDetails;